import { useState, useEffect, useCallback } from 'react';
import { ApiError } from '../types';
import { Member } from '../context/OrganizationContext';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import { extractErrorMessage } from '../utils/error-handler';
import { toContextMembers } from '../components/team/utils/adapters';

export function useTeamMembers(teamId: string, organizationId?: string) {
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  
  const fetchMembers = useCallback(async () => {
    if (!teamId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`Fetching members for team: ${teamId}`);
      const response = await teamService.getTeamMembers(teamId, 1, 100); // Get all members
      
      // Convert API members to context members with required fields
      const contextMembers = toContextMembers(response.data || []);
      
      setMembers(contextMembers);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [teamId]);
  
  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);
  
  const inviteMember = async (email: string, roleId: string, personalMessage?: string) => {
    try {
      setIsLoading(true);
      await teamService.createTeamInvitation(teamId, {
        invitee_email: email,
        role: roleId,
        personal_message: personalMessage
      });
      
      await fetchMembers(); // Refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      
      // Provide more user-friendly error messages
      let friendlyError = { ...error };
      if (error.status === 409 && error.code === 'RESOURCE_ALREADY_EXISTS') {
        friendlyError.message = `${email} has already been invited to this team. Please check pending invitations or try a different email address.`;
      } else if (error.status === 403) {
        friendlyError.message = 'You do not have permission to invite members to this team.';
      } else if (error.status === 400) {
        friendlyError.message = 'Invalid invitation data. Please check the email address and try again.';
      } else if (!error.message || error.message.includes('SERVICE_ERROR') || error.message.includes('Failed to')) {
        friendlyError.message = 'Failed to send invitation. Please try again later.';
      }
      
      setError(friendlyError);
      return { success: false, error: friendlyError };
    } finally {
      setIsLoading(false);
    }
  };
  
  const updateMemberRole = async (memberId: string, roleId: string) => {
    try {
      setIsLoading(true);
      await teamService.updateTeamMemberRole(teamId, memberId, roleId);
      
      await fetchMembers(); // Refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };
  
  const removeMember = async (memberId: string) => {
    try {
      setIsLoading(true);
      await teamService.removeTeamMember(teamId, memberId);
      
      await fetchMembers(); // Refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    members,
    isLoading,
    error,
    refreshMembers: fetchMembers,
    inviteMember,
    updateMemberRole,
    removeMember
  };
}

export default useTeamMembers;